import React from 'react';

import {isBrowser} from 'lib/Browser';
import {readableTime} from 'util/DateUtil';
import fuzzydate from 'util/fuzzydate';

interface DelayedStringFormatterProps {
  format: (s: string) => string;
  titleFormatFn: (s: string) => string;
  iso8601String: string;
}

interface State {
  formattedString: string;
  formattedTitle: string;
}

/**
 * Renders format(string) in the next tick, so as to avoid blocking main thread
 *
 * This is useful as format() tends to call moment.js, which is slow
 */
function makeDelayedStringFormatter(
  updateInterval: number = 0
): React.ComponentType<DelayedStringFormatterProps> {
  return class extends React.Component<DelayedStringFormatterProps, State> {
    _intervalId?: ReturnType<typeof setInterval>;

    constructor(props: DelayedStringFormatterProps) {
      super(props);
      // Eagerly format it if we're not in the browser
      this.state = isBrowser
        ? {formattedString: '', formattedTitle: ''}
        : this._getFormattedStringAndTitleState();
    }

    componentDidMount(): void {
      setTimeout(this._setFormattedStringAndTitle, 0);
      if (updateInterval !== 0) {
        this._intervalId = setInterval(this._setFormattedStringAndTitle, updateInterval * 1000);
      }
    }

    shouldComponentUpdate = (nextProps: DelayedStringFormatterProps, nextState: State): boolean =>
      this.props.format !== nextProps.format ||
      this.props.iso8601String !== nextProps.iso8601String ||
      this.state.formattedString !== nextState.formattedString;

    componentWillUnmount(): void {
      if (this._intervalId) {
        clearInterval(this._intervalId);
      }
    }

    _getFormattedStringAndTitleState = (): State => ({
      formattedString: this.props.format(this.props.iso8601String),
      formattedTitle: this.props.titleFormatFn(this.props.iso8601String),
    });

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    _setFormattedStringAndTitle = () => {
      this.setState(this._getFormattedStringAndTitleState());
    };

    render(): JSX.Element {
      return (
        <time
          title={this.state.formattedTitle}
          dateTime={this.props.iso8601String}
          data-test-original-value={this.props.iso8601String}
        >
          {this.state.formattedString}
        </time>
      );
    }
  };
}

function fuzzyFormat(datetime: string): string {
  return fuzzydate(datetime, {days: 7});
}

const DelayedStringFormatterWithUpdate = makeDelayedStringFormatter(30);

/**
 * Render relative time for recent times (< 7 days), otherwise renders LocalTime
 *
 * Updates itself "live" (every 30s)
 *
 * @param {string} params.datetime - an ISO-8601 formatted string
 */
export function RelativeTime(params: {datetime: string}): JSX.Element {
  return (
    <DelayedStringFormatterWithUpdate
      format={fuzzyFormat}
      titleFormatFn={readableTime}
      iso8601String={params.datetime}
    />
  );
}

const DelayedStringFormatter = makeDelayedStringFormatter();

/**
 * Renders datetime in a human-friendly format, in the browser timezone.
 *
 * @param {string} params.datetime - an ISO-8601 formatted string
 */
export function LocalTime(params: {datetime: string}): JSX.Element {
  return (
    <DelayedStringFormatter
      format={readableTime}
      titleFormatFn={readableTime}
      iso8601String={params.datetime}
    />
  );
}
